import type { SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { NumberField, Callout } from "@octopusdeploy/design-system-components";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { GetTeamsLookupBffResponseTeam, TagSetResource, UserResource, Repository, ApprovalPolicyResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { EnableExtendedTagSetsCallout } from "~/areas/library/components/ApprovalPolicies/EnableExtendedTagSetsCallout";
import { repository } from "~/clientInstance";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { TagSetMultiSelect } from "~/components/MultiSelect/TagSetMultiSelect";
import { TeamMultiSelect } from "~/components/MultiSelect/TeamMultiSelect";
import { UserMultiSelect } from "~/components/MultiSelect/UserMultiSelect";
import InternalLink from "~/components/Navigation/InternalLink/index";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { Form, useForm } from "~/components/form/Form/Form";
import { ExpandableFormSection, required, Summary, Text } from "~/components/form/index";
interface CreateOrEditApprovalPolicyPageQueryParams {
    spaceId: string;
    id?: string;
}
export interface ApprovalPolicyPageLoaderData {
    approvalPolicy: ApprovalPolicyResource;
    projectTagSets: TagSetResource[];
    environmentTagSets: TagSetResource[];
    teams: GetTeamsLookupBffResponseTeam[];
    users: UserResource[];
}
export async function approvalPolicyPageLoader(repository: Repository, routeParams: CreateOrEditApprovalPolicyPageQueryParams): Promise<ApprovalPolicyPageLoaderData> {
    const projectTagSets = repository.TagSets.getAllByScopes(["Project"]);
    const environmentTagSets = repository.TagSets.getAllByScopes(["Environment"]);
    const teams = repository.Teams.lookupBff();
    const users = repository.Users.all();
    return {
        approvalPolicy: routeParams.id === undefined ? getDefaultApprovalPolicy(routeParams.spaceId) : await repository.ApprovalPolicies.get(routeParams.id),
        projectTagSets: await projectTagSets,
        environmentTagSets: await environmentTagSets,
        teams: await teams,
        users: await users,
    };
}
interface CreateApprovalPolicyPageProps {
    spaceId: string;
    create: true;
    loaderData: ApprovalPolicyPageLoaderData;
    id: undefined;
}
export interface EditApprovalPolicyPageProps {
    spaceId: string;
    create: false;
    id: string;
    loaderData: ApprovalPolicyPageLoaderData;
}
type CreateOrEditApprovalPolicyPageProps = CreateApprovalPolicyPageProps | EditApprovalPolicyPageProps;
const getDefaultApprovalPolicy = (spaceId: string): ApprovalPolicyResource => {
    return {
        Id: "",
        SpaceId: spaceId,
        Name: "",
        Description: "",
        ProjectTags: [],
        EnvironmentTags: [],
        ApprovingUserIds: [],
        ApprovingTeamIds: [],
        MinimumApproversRequired: 2,
    };
};
export function CreateOrEditApprovalPolicyPage({ spaceId, create, loaderData, id }: CreateOrEditApprovalPolicyPageProps) {
    const { navigate } = useSpaceAwareNavigation();
    const { status, doBusyTask } = useLegacyDoBusyTask();
    const isExtendedTagSetsEnabled = isFeatureToggleEnabled("extended-tag-sets");
    const initialModel = create ? getDefaultApprovalPolicy(spaceId) : loaderData.approvalPolicy;
    const { model, isSubmitting, setModel, formProps, createSaveAction, submit, getFieldError } = useForm({
        formName: "Save Approval Policy",
        initialModel: initialModel,
        onSubmit: (repository, resource) => (create ? repository.ApprovalPolicies.create(resource) : repository.ApprovalPolicies.modify(resource)),
        updateFormModelAfterSubmit: (model) => model,
        afterSubmit: async (_, createdPolicy) => {
            navigate(links.approvalPolicyPage.generateUrl({ spaceId, id: createdPolicy.Id }));
        },
        snackbarSaveText: "Approval Policy updated",
        // submitPermission: { }, // TODO: needs a proper permission
    });
    const { execute: deleteApprovalPolicy } = useMutation({
        name: "deleteApprovalPolicy",
        action: async (repo) => id && (await repository.ApprovalPolicies.delete(id)),
        afterComplete: async () => navigate(links.approvalPoliciesPage.generateUrl({ spaceId })),
    });
    const overflowActions: SimpleMenuItem[] = [];
    if (!create) {
        const isAllowedToDeletePolicy = true; // TODO: needs a proper permission
        const isAllowedToViewAudit = isAllowed({
            permission: Permission.EventView,
            wildcard: true,
        });
        if (isAllowedToDeletePolicy) {
            overflowActions.push({
                type: "button",
                label: "Delete",
                onClick: () => {
                    deleteApprovalPolicy();
                    navigate(links.approvalPoliciesPage.generateUrl({ spaceId }));
                },
            });
        }
        if (isAllowedToViewAudit) {
            overflowActions.push({
                type: "internal-link",
                label: "Audit Trail",
                path: links.auditPage.generateUrl({ regardingAny: [], documentTypes: ["ApprovalPolicies"] }),
            });
        }
    }
    const tagsHelpSummary = (tagSets: TagSetResource[], tagType: string) => {
        if (noTagSets(tagSets)) {
            return `No ${tagType} Tag Sets available.`;
        }
        if (emptyTags(tagSets)) {
            return `${tagType} Tag Sets are empty.`;
        }
        return `Select ${tagType} tags to apply to this approval policy`;
    };
    const noTagSets = (tagSets: TagSetResource[]) => tagSets.length === 0;
    const emptyTags = (tagSets: TagSetResource[]) => tagSets.flatMap((ts) => ts.Tags).length === 0;
    function getTagSetsCallout(tagSets: TagSetResource[], tagType: string) {
        if (!isExtendedTagSetsEnabled) {
            return <EnableExtendedTagSetsCallout />;
        }
        if (noTagSets(tagSets)) {
            return (<Callout type={"information"} title={`No ${tagType} scoped Tag Sets`}>
                    Add {tagType} scoped <InternalLink to={links.tagSetsPage.generateUrl({ spaceId: spaceId })}>Tag Sets</InternalLink> now.
                </Callout>);
        }
        if (emptyTags(tagSets)) {
            return (<Callout type={"information"} title={`${tagType} scoped Tag Sets Are Empty`}>
                    Add tags to your <InternalLink to={links.tagSetsPage.generateUrl({ spaceId: spaceId }, { scopes: [tagType] })}>{tagType} scoped Tag Sets</InternalLink> now.
                </Callout>);
        }
        return <></>;
    }
    return (model && (<PageContent header={{
            primaryAction: createSaveAction(),
            title: create ? "Create Approval Policy" : initialModel?.Name,
            breadcrumbs: [{ label: "Approval Policies", pageUrl: links.approvalPoliciesPage.generateUrl({ spaceId }) }],
            overflowActions: overflowActions,
        }}>
                <Form {...formProps}>
                    <ExpandableFormSection errorKey="Name" title="Name" focusOnExpandAll summary={model.Name ? Summary.summary(model.Name) : Summary.placeholder("Please enter a name for your approval policy")} help="A short, memorable, unique name for this approval policy.">
                        <Text key={"Name"} value={model.Name} onChange={(Name) => setModel((prev) => ({ ...prev, Name }))} label="Name" validate={required("Please enter an approval policy name")} autoFocus={true} error={getFieldError("Name")}/>
                    </ExpandableFormSection>

                    <ExpandableFormSection errorKey="Description" title="Description" focusOnExpandAll summary={model.Description ? Summary.summary(model.Description) : Summary.placeholder("No description provided")} help="A description for this approval policy.">
                        <Text value={model.Description} onChange={(Description) => setModel((prev) => ({ ...prev, Description }))} label="Description" autoFocus={true}/>
                    </ExpandableFormSection>

                    <ExpandableFormSection errorKey="ProjectTags" title="Project Tags" focusOnExpandAll summary={model.ProjectTags.length > 0 ? Summary.summary(`${model.ProjectTags.length} tag(s) selected`) : Summary.placeholder("No project tags selected")} help={tagsHelpSummary(loaderData.projectTagSets, "Project")}>
                        {getTagSetsCallout(loaderData.projectTagSets, "Project")}
                        {isExtendedTagSetsEnabled && (<TagSetMultiSelect items={loaderData.projectTagSets} value={model.ProjectTags} scopes={["Project"]} showFreeTextTagSet={true} onChange={(tags) => setModel((prev) => ({ ...prev, ProjectTags: tags }))} doBusyTask={doBusyTask} disableCloseOnSelected={true}/>)}
                    </ExpandableFormSection>

                    <ExpandableFormSection errorKey="EnvironmentTags" title="Environment Tags" focusOnExpandAll summary={model.EnvironmentTags.length > 0 ? Summary.summary(`${model.EnvironmentTags.length} tag(s) selected`) : Summary.placeholder("No environment tags selected")} help={tagsHelpSummary(loaderData.environmentTagSets, "Environment")}>
                        {getTagSetsCallout(loaderData.environmentTagSets, "Environment")}
                        {isExtendedTagSetsEnabled && (<TagSetMultiSelect items={loaderData.environmentTagSets} value={model.EnvironmentTags} scopes={["Environment"]} showFreeTextTagSet={true} onChange={(tags) => setModel((prev) => ({ ...prev, EnvironmentTags: tags }))} doBusyTask={doBusyTask} disableCloseOnSelected={true}/>)}
                    </ExpandableFormSection>

                    <ExpandableFormSection errorKey="MinimumApproversRequired" title="Minimum Approvers Required" focusOnExpandAll summary={Summary.summary(`${model.MinimumApproversRequired} minimum approver(s) required`)} help="Set a number of minimum approvers required to approve deployments">
                        <NumberField key="MinimumApproversRequired" description="Select a number between 0 and 255" label="Minimum Approvers Required" value={model.MinimumApproversRequired} min={0} max={100} name="MinimumApproversRequired" onChange={(minimumApprovers) => setModel({ ...model, MinimumApproversRequired: minimumApprovers ?? undefined })} error={getFieldError("MinimumApproversRequired")} autoFocus/>
                    </ExpandableFormSection>

                    <ExpandableFormSection errorKey="Approvers" title="Approvers" focusOnExpandAll summary={model.ApprovingTeamIds.length > 0 || model.ApprovingUserIds.length > 0
            ? Summary.summary(`${model.ApprovingTeamIds?.concat(model.ApprovingUserIds).length} approver(s) selected`)
            : Summary.placeholder("No approvers selected")} help="Select authorized teams or users to approve deployments">
                        <TeamMultiSelect items={loaderData.teams} value={model.ApprovingTeamIds} onChange={(teams) => setModel({ ...model, ApprovingTeamIds: teams })}/>
                        <UserMultiSelect items={loaderData.users} value={model.ApprovingUserIds} onChange={(users) => setModel({ ...model, ApprovingUserIds: users })}/>
                    </ExpandableFormSection>
                </Form>
            </PageContent>));
}
const approvalPolicyPageTitle = "ApprovalPolicyPage";
